import { z } from "zod";

const subscriptionStatuses = [
  "CHECKOUT_PENDING",
  "ACTIVE",
  "PAST_DUE",
  "CANCELLATION_PENDING",
  "CANCELED",
  "EXPIRED",
  "SYNC_ERROR",
] as const;
export const subscriptionStatusSchema = z.enum(subscriptionStatuses);

const providerStatuses = [
  "ACTIVE",
  "PENDING",
  "UNPAID",
  "UPGRADED",
  "CANCELED",
  "EXPIRED",
] as const;
export const providerStatusSchema = z.enum(providerStatuses);

const frequencies = ["WEEKLY", "MONTHLY", "YEARLY"] as const;
export const frequencySchema = z.enum(frequencies);
export const currencySchema = z.literal("TRY");

export const addressSchema = z.object({
  firstName: z.string().trim().min(2, "Ad en az 2 karakter olmalı.").max(80),
  lastName: z.string().trim().min(2, "Soyad en az 2 karakter olmalı.").max(80),
  phone: z
    .string()
    .trim()
    .regex(/^(?:\+90|0)?5\d{9}$/u, "Geçerli bir Türkiye cep telefonu girin."),
  address1: z.string().trim().min(5, "Açık adres en az 5 karakter olmalı.").max(240),
  address2: z.string().trim().max(160).optional(),
  city: z.string().trim().min(2, "İl girin.").max(80),
  district: z.string().trim().min(2, "İlçe girin.").max(80),
  province: z.string().trim().min(2).max(80),
  postalCode: z
    .string()
    .trim()
    .regex(/^\d{5}$/u, "Posta kodu 5 haneli olmalı.")
    .optional()
    .or(z.literal("")),
  countryCode: z.literal("TR"),
});

export const planSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string().optional(),
  variantLabel: z.string().min(1),
  shopifyVariantId: z.string().regex(/^\d+$/u),
  sku: z.string().optional(),
  imageUrl: z.string().url().optional(),
  frequency: frequencySchema,
  intervalCount: z.number().int().positive(),
  amountMinor: z.number().int().nonnegative(),
  currency: currencySchema,
  shippingIncluded: z.boolean(),
  active: z.boolean(),
  version: z.number().int().positive(),
});

export const orderSummarySchema = z.object({
  name: z.string().min(1),
  adminUrl: z.string().url().optional(),
  customerUrl: z.string().url().optional(),
  createdAt: z.string().datetime(),
});

export const cycleSchema = z.object({
  id: z.string().min(1),
  cycleNumber: z.number().int().positive(),
  paidAt: z.string().datetime().optional(),
  scheduledAt: z.string().datetime().optional(),
  amountMinor: z.number().int().nonnegative(),
  currency: currencySchema,
  paymentStatus: z.enum(
    ["PENDING", "SUCCEEDED", "FAILED", "REFUNDED"] as const,
  ),
  shopifySyncStatus: z.enum([
    "NOT_STARTED",
    "QUEUED",
    "PROCESSING",
    "SUCCEEDED",
    "FAILED_RETRYABLE",
    "FAILED_PERMANENT",
  ] as const),
  order: orderSummarySchema.optional(),
  attemptCount: z.number().int().nonnegative(),
  lastErrorCode: z.string().optional(),
  lastErrorMessage: z.string().optional(),
});

export const subscriptionSummarySchema = z.object({
  id: z.string().min(1),
  customerUid: z.string().min(1),
  plan: planSchema,
  status: subscriptionStatusSchema,
  amountMinor: z.number().int().nonnegative(),
  currency: currencySchema,
  nextPaymentAt: z.string().datetime().optional(),
  startedAt: z.string().datetime().optional(),
  lastOrder: orderSummarySchema.optional(),
});

export const subscriptionDetailSchema = subscriptionSummarySchema.extend({
  reference: z.string().min(1),
  providerStatus: providerStatusSchema,
  shippingAddress: addressSchema,
  cycles: z.array(cycleSchema),
  canceledAt: z.string().datetime().optional(),
});

export const subscriptionsResponseSchema = z.object({
  items: z.array(subscriptionSummarySchema),
});

export const plansResponseSchema = z.object({ items: z.array(planSchema) });

export const adminPlanSchema = planSchema.extend({
  shopifyProductId: z.string().regex(/^\d+$/u).optional(),
  shopifyProductGid: z.string().regex(/^gid:\/\/shopify\/Product\/\d+$/u).optional(),
  shopifyVariantId: z.string().regex(/^\d+$/u),
  shopifyVariantGid: z
    .string()
    .regex(/^gid:\/\/shopify\/ProductVariant\/\d+$/u)
    .optional(),
  iyzicoProductReferenceCode: z.string().min(1).optional(),
  iyzicoPricingPlanReferenceCode: z.string().min(1).optional(),
  shippingPriceMinor: z.number().int().nonnegative().optional(),
});

export const adminPlansResponseSchema = z.object({ items: z.array(adminPlanSchema) });

export const shopifyCatalogVariantSchema = z.object({
  id: z.string().regex(/^gid:\/\/shopify\/ProductVariant\/\d+$/u),
  title: z.string().min(1),
  sku: z.string().optional(),
  amountMinor: z.number().int().nonnegative(),
  currency: currencySchema,
  availableForSale: z.boolean(),
});

export const shopifyCatalogProductSchema = z.object({
  id: z.string().regex(/^gid:\/\/shopify\/Product\/\d+$/u),
  title: z.string().min(1),
  variants: z.array(shopifyCatalogVariantSchema),
});

export const iyzicoCatalogProductSchema = z.object({
  referenceCode: z.string().min(1),
  name: z.string().min(1),
  description: z.string().optional(),
  status: z.string().min(1),
});

export const iyzicoCatalogPricingPlanSchema = z.object({
  referenceCode: z.string().min(1),
  productReferenceCode: z.string().min(1),
  name: z.string().min(1),
  amountMinor: z.number().int().nonnegative(),
  currency: currencySchema,
  paymentInterval: frequencySchema,
  paymentIntervalCount: z.number().int().positive(),
  status: z.string().min(1),
});

export const planCatalogSchema = z.object({
  shopifyProducts: z.array(shopifyCatalogProductSchema),
  iyzicoProducts: z.array(iyzicoCatalogProductSchema),
  iyzicoPricingPlans: z.array(iyzicoCatalogPricingPlanSchema),
});

export const adminPlanInputSchema = z
  .object({
    planId: z.string().min(1).optional(),
    name: z.string().trim().min(1, "Plan adı girin.").max(160),
    description: z.string().trim().max(1_000).optional(),
    shopifyProductId: z.string().regex(/^\d+$/u),
    shopifyProductGid: z.string().regex(/^gid:\/\/shopify\/Product\/\d+$/u),
    shopifyVariantId: z.string().regex(/^\d+$/u),
    shopifyVariantGid: z.string().regex(/^gid:\/\/shopify\/ProductVariant\/\d+$/u),
    iyzicoProductReferenceCode: z.string().min(1, "iyzico ürünü seçin."),
    iyzicoPricingPlanReferenceCode: z.string().min(1, "iyzico fiyat planı seçin."),
    frequency: frequencySchema,
    intervalCount: z.number().int().positive().max(100),
    amountMinor: z.number().int().nonnegative(),
    currency: currencySchema,
    shippingPriceMinor: z.number().int().nonnegative(),
    shippingIncluded: z.literal(true),
    active: z.boolean(),
    version: z.number().int().positive(),
  })
  .superRefine((value, context) => {
    if (value.shippingPriceMinor > value.amountMinor) {
      context.addIssue({
        code: "custom",
        message: "Kargo payı toplam tutardan büyük olamaz.",
        path: ["shippingPriceMinor"],
      });
    }
  });

export const createIyzicoProductInputSchema = z.object({
  name: z.string().trim().min(1, "Ürün adı girin.").max(150),
  description: z.string().trim().max(500).optional(),
});

export const createIyzicoPricingPlanInputSchema = z.object({
  productReferenceCode: z.string().min(1),
  name: z.string().trim().min(1, "Fiyat planı adı girin.").max(150),
  amountMinor: z.number().int().positive(),
  currency: currencySchema,
  paymentInterval: frequencySchema,
  paymentIntervalCount: z.number().int().positive().max(100),
});

export const exchangeResponseSchema = z.object({
  customToken: z.string().min(1),
  redirectPath: z.string().startsWith("/"),
});

export const checkoutResponseSchema = z.object({
  subscriptionId: z.string().min(1),
  checkoutUrl: z.string().url(),
});

export const paymentResultSchema = z.object({
  subscriptionId: z.string().min(1),
  status: z.enum(["PROCESSING", "SUCCEEDED", "FAILED"]),
  message: z.string().min(1),
  accountUrl: z.string().url().optional(),
  storefrontUrl: z.string().url().optional(),
});

export const actionResponseSchema = z.object({
  success: z.literal(true),
  message: z.string().min(1),
});

export const legalDocumentSchema = z.object({
  type: z.enum(["subscription_terms", "privacy_policy", "distance_sales_placeholder"]),
  version: z.string().min(1),
  contentUrl: z.string().url().optional(),
  active: z.boolean(),
  publishedAt: z.string().datetime().optional(),
  content: z.string().optional(),
  requiresLegalReview: z.boolean(),
});

function isValidTurkishIdentityNumber(value: string): boolean {
  if (!/^[1-9]\d{10}$/u.test(value)) return false;
  const digits = [...value].map(Number);
  const digit = (index: number): number => value.charCodeAt(index) - 48;
  const oddSum = digit(0) + digit(2) + digit(4) + digit(6) + digit(8);
  const evenSum = digit(1) + digit(3) + digit(5) + digit(7);
  return (
    (oddSum * 7 - evenSum) % 10 === digit(9) &&
    digits.slice(0, 10).reduce((sum, current) => sum + current, 0) % 10 === digit(10)
  );
}

/**
 * The plan is identified by the single-use session token minted when the customer
 * pressed "Abone Ol" on the product page, never by a client-supplied plan id or price.
 */
export const newSubscriptionInputSchema = z.object({
  checkoutSessionToken: z.string().min(32, "Ödeme oturumu geçersiz."),
  /** Misafir ödemesinde zorunlu: hesap bu adrese açılır. */
  email: z.string().trim().email("Geçerli bir e-posta adresi girin.").max(254).optional(),
  identityNumber: z
    .string()
    .trim()
    .refine(isValidTurkishIdentityNumber, "Geçerli bir T.C. kimlik numarası girin."),
  shippingAddress: addressSchema,
  consentAccepted: z.boolean().refine((value) => value, {
    message: "Abonelik koşullarını kabul etmelisiniz.",
  }),
  termsVersion: z.string().min(1),
  privacyVersion: z.string().min(1),
  distanceSalesVersion: z.string().min(1),
  /** Unchecked by default; writing to the Shopify account requires an explicit opt-in. */
  saveAddressToShopify: z.boolean().default(false),
});

/* ---------------------------------------------------------------------------
 * Checkout session view model
 * ------------------------------------------------------------------------- */

export const brandingSchema = z.object({
  logoUrl: z.string().url(),
  logoAlt: z.string().min(1),
  faviconUrl: z.string().url().optional(),
  primaryColor: z.string().min(4),
  primaryHoverColor: z.string().min(4),
  primaryTextColor: z.string().min(4),
  backgroundColor: z.string().min(4),
  surfaceColor: z.string().min(4),
  surfaceMutedColor: z.string().min(4),
  textColor: z.string().min(4),
  mutedTextColor: z.string().min(4),
  headingColor: z.string().min(4),
  borderColor: z.string().min(4),
  successColor: z.string().min(4),
  criticalColor: z.string().min(4),
  bodyFontFamily: z.string().min(1),
  headingFontFamily: z.string().min(1),
  buttonRadius: z.string().min(2),
  cardRadius: z.string().min(2),
  inputRadius: z.string().min(2),
  badgeRadius: z.string().min(2),
  containerWidth: z.string().min(4),
});

export const checkoutOfferSchema = z.object({
  offerId: z.string().min(1),
  planVersion: z.number().int().positive(),
  shopifyVariantId: z.string().regex(/^\d+$/u),
  productTitle: z.string().min(1),
  variantTitle: z.string().min(1),
  imageUrl: z.string().url().optional(),
  frequencyUnit: z.enum(["WEEK", "MONTH"]),
  intervalCount: z.number().int().positive(),
  frequencyLabel: z.string().min(1),
  displayTitle: z.string().min(1),
  displayDescription: z.string().optional(),
  badgeText: z.string().optional(),
  regularPriceMinor: z.number().int().nonnegative(),
  regularPriceLabel: z.string().min(1),
  subscriptionPriceMinor: z.number().int().nonnegative(),
  subscriptionPriceLabel: z.string().min(1),
  discountAmountMinor: z.number().int().nonnegative(),
  discountAmountLabel: z.string().min(1),
  discountPercentage: z.number().int().min(0).max(100),
  hasDiscount: z.boolean(),
  shippingIncluded: z.boolean(),
  currency: currencySchema,
});

export const addressChoiceSchema = z.object({
  id: z.string().min(1),
  source: z.enum([
    "CHECKOUT_SESSION",
    "SHOPIFY_DEFAULT",
    "SHOPIFY_FIRST",
    "PREVIOUS_SUBSCRIPTION",
    "EMPTY",
  ]),
  label: z.string().min(1),
  isDefault: z.boolean(),
  address: addressSchema.partial().optional(),
  missingFields: z.array(z.string()),
});

export const checkoutViewSchema = z.object({
  offer: checkoutOfferSchema,
  /** Arkasında hesap olmayan ödeme: iletişim bilgilerini müşteri kendisi giriyor. */
  guest: z.boolean().optional(),
  summaryLines: z.array(
    z.object({
      label: z.string().min(1),
      value: z.string().min(1),
      emphasis: z.enum(["STRIKETHROUGH", "SAVING", "TOTAL"]).optional(),
    }),
  ),
  contact: z.object({
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    email: z.string().optional(),
    phone: z.string().optional(),
    missingFields: z.array(z.string()),
  }),
  addresses: z.object({
    choices: z.array(addressChoiceSchema),
    selectedId: z.string().optional(),
    selectedSource: addressChoiceSchema.shape.source,
    selectedAddress: addressSchema.partial().optional(),
    showPicker: z.boolean(),
    shopifyAddressAccessDenied: z.boolean(),
  }),
  branding: brandingSchema,
  expiresAt: z.string().datetime(),
  changeSelectionUrl: z.string().url(),
  legal: z.object({
    termsVersion: z.string().optional(),
    privacyVersion: z.string().optional(),
    distanceSalesVersion: z.string().optional(),
  }),
});

export const addressUpdateInputSchema = addressSchema;

export const cardUpdateInputSchema = z.object({
  confirmation: z.boolean().refine((value) => value, {
    message: "Güvenli kart güncelleme formuna geçişi onaylayın.",
  }),
});

export const cancelInputSchema = z.object({
  confirmation: z.boolean().refine((value) => value, {
    message: "İptal sonuçlarını anladığınızı onaylayın.",
  }),
  reason: z.string().trim().max(500).optional(),
});

export const adminDashboardSchema = z.object({
  activeSubscriptions: z.number().int().nonnegative(),
  pastDueSubscriptions: z.number().int().nonnegative(),
  successfulPaymentsToday: z.number().int().nonnegative(),
  monthlyRevenueMinor: z.number().int().nonnegative(),
  pendingOrderSync: z.number().int().nonnegative(),
  failedOrderSync: z.number().int().nonnegative(),
  recentWebhookErrors: z.array(
    z.object({
      id: z.string(),
      eventType: z.string(),
      occurredAt: z.string().datetime(),
      message: z.string(),
      correlationId: z.string(),
    }),
  ),
});

export const adminSubscriptionSchema = subscriptionDetailSchema.extend({
  customer: z.object({
    name: z.string(),
    email: z.string().email(),
    shopifyCustomerId: z.string(),
  }),
  webhookEvents: z.array(
    z.object({
      id: z.string(),
      eventType: z.string(),
      status: z.enum(["RECEIVED", "PROCESSING", "PROCESSED", "FAILED"]),
      receivedAt: z.string().datetime(),
    }),
  ),
  auditEntries: z
    .array(
      z.object({
        id: z.string(),
        actorType: z.enum(["CUSTOMER", "ADMIN", "SYSTEM"]),
        actorLabel: z.string(),
        action: z.string(),
        resourceType: z.string(),
        resourceId: z.string().optional(),
        correlationId: z.string(),
        createdAt: z.string().datetime(),
      }),
    )
    .optional(),
});

export const adminSubscriptionSummarySchema = subscriptionSummarySchema.extend({
  reference: z.string().min(1),
  providerStatus: providerStatusSchema,
  customer: z.object({
    name: z.string(),
    email: z.string().email(),
    shopifyCustomerId: z.string(),
  }),
});

export const adminSubscriptionsResponseSchema = z.object({
  items: z.array(adminSubscriptionSummarySchema),
});

export const paymentIssueSchema = z.object({
  cycleId: z.string(),
  subscriptionId: z.string(),
  customerName: z.string(),
  planName: z.string(),
  errorCode: z.string(),
  errorMessage: z.string(),
  lastAttemptAt: z.string().datetime(),
  retryAllowed: z.boolean(),
  reviewRequired: z.boolean(),
});

export const paymentIssuesResponseSchema = z.object({ items: z.array(paymentIssueSchema) });

export const orderSyncIssueSchema = z.object({
  cycleId: z.string(),
  subscriptionId: z.string(),
  orderReference: z.string(),
  errorMessage: z.string(),
  attemptCount: z.number().int().nonnegative(),
  lastAttemptAt: z.string().datetime(),
  retryAllowed: z.boolean(),
  canMarkPermanent: z.boolean(),
});

export const orderSyncIssuesResponseSchema = z.object({
  items: z.array(orderSyncIssueSchema),
});

export const customerSummarySchema = z.object({
  uid: z.string(),
  name: z.string(),
  email: z.string().email(),
  shopifyCustomerId: z.string(),
  status: z.enum(["ACTIVE", "REDACTED"]),
  activeSubscriptionCount: z.number().int().nonnegative(),
  lastLoginAt: z.string().datetime().optional(),
});

export const customersResponseSchema = z.object({ items: z.array(customerSummarySchema) });

export const auditEntrySchema = z.object({
  id: z.string(),
  actorType: z.enum(["CUSTOMER", "ADMIN", "SYSTEM"]),
  actorLabel: z.string(),
  action: z.string(),
  resourceType: z.string(),
  resourceId: z.string().optional(),
  correlationId: z.string(),
  createdAt: z.string().datetime(),
});

export const auditResponseSchema = z.object({ items: z.array(auditEntrySchema) });

export const adminSettingsSchema = z.object({
  sendShopifyReceipt: z.boolean(),
  supportEmail: z.string().email(),
  defaultCurrency: currencySchema,
  timezone: z.literal("Europe/Istanbul"),
  environment: z.enum(["staging", "production"]),
  appCheckEnforced: z.boolean(),
});

export const adminSettingsInputSchema = adminSettingsSchema.pick({
  sendShopifyReceipt: true,
  supportEmail: true,
});

/* ---------------------------------------------------------------------------
 * Admin product catalog and offers
 * ------------------------------------------------------------------------- */

export const adminOfferSchema = z.object({
  id: z.string().min(1),
  version: z.number().int().positive(),
  shopifyProductGid: z.string().min(1),
  shopifyVariantGid: z.string().min(1),
  shopifyVariantId: z.string().regex(/^\d+$/u),
  frequencyUnit: z.enum(["WEEK", "MONTH"]),
  intervalCount: z.number().int().positive(),
  frequencyLabel: z.string().min(1),
  displayTitle: z.string().min(1),
  displayDescription: z.string().optional(),
  badgeText: z.string().optional(),
  regularPriceMinor: z.number().int().nonnegative(),
  subscriptionPriceMinor: z.number().int().nonnegative(),
  shippingPriceMinor: z.number().int().nonnegative(),
  discountAmountMinor: z.number().int().nonnegative(),
  discountPercentage: z.number().int().min(0).max(100),
  active: z.boolean(),
  defaultForVariant: z.boolean(),
  sortOrder: z.number().int().nonnegative(),
  iyzicoProductReferenceCode: z.string().min(1),
  iyzicoPricingPlanReferenceCode: z.string().min(1),
  supersededByPlanId: z.string().optional(),
  priceDrift: z
    .object({
      liveRegularPriceMinor: z.number().int().nonnegative(),
      storedRegularPriceMinor: z.number().int().nonnegative(),
      differenceMinor: z.number().int(),
      detectedAt: z.string().datetime().optional(),
    })
    .optional(),
});

export const adminCatalogVariantSchema = z.object({
  shopifyVariantGid: z.string().min(1),
  shopifyVariantId: z.string().regex(/^\d+$/u),
  title: z.string().min(1),
  sku: z.string().optional(),
  imageUrl: z.string().url().optional(),
  regularPriceMinor: z.number().int().nonnegative(),
  currency: currencySchema,
  availableForSale: z.boolean(),
  inventoryQuantity: z.number().int().optional(),
  inventoryPolicy: z.enum(["CONTINUE", "DENY"]).optional(),
  subscriptionEnabled: z.boolean(),
  activeOfferCount: z.number().int().nonnegative(),
  offers: z.array(adminOfferSchema),
});

export const adminCatalogProductSchema = z.object({
  shopifyProductGid: z.string().min(1),
  shopifyProductId: z.string().regex(/^\d+$/u),
  title: z.string().min(1),
  handle: z.string().optional(),
  status: z.string().optional(),
  imageUrl: z.string().url().optional(),
  subscriptionEnabled: z.boolean(),
  subscriptionOnly: z.boolean(),
  activeOfferCount: z.number().int().nonnegative(),
  variants: z.array(adminCatalogVariantSchema),
});

export const adminProductCatalogSchema = z.object({
  products: z.array(adminCatalogProductSchema),
  nextCursor: z.string().optional(),
  lastSyncedAt: z.string().datetime().optional(),
});

export const adminOfferInputSchema = z.object({
  offerId: z.string().min(1).optional(),
  expectedVersion: z.number().int().positive().optional(),
  shopifyProductGid: z.string().min(1),
  shopifyVariantGid: z.string().min(1),
  frequencyUnit: z.enum(["WEEK", "MONTH"]),
  intervalCount: z.number().int().min(1).max(52),
  displayTitle: z.string().trim().max(160).optional(),
  displayDescription: z.string().trim().max(500).optional(),
  badgeText: z.string().trim().max(40).optional(),
  subscriptionPriceMinor: z.number().int().nonnegative(),
  shippingPriceMinor: z.number().int().nonnegative(),
  iyzicoProductReferenceCode: z.string().min(1),
  iyzicoPricingPlanReferenceCode: z.string().min(1),
  active: z.boolean(),
  defaultForVariant: z.boolean(),
  sortOrder: z.number().int().min(0).max(10_000),
});

export const adminOfferUpsertResponseSchema = z.object({
  offer: adminOfferSchema,
  repricedFrom: z.string().optional(),
});

export const adminOfferRepriceInputSchema = z.object({
  expectedVersion: z.number().int().positive(),
  newSubscriptionPriceMinor: z.number().int().min(100).max(100_000_000),
});

export const adminOfferRepriceResponseSchema = z.object({
  offer: adminOfferSchema,
  repricedFrom: z.string().optional(),
  providerPlanCreated: z.string().optional(),
});

export const adminOfferSyncResponseSchema = z.object({ offer: adminOfferSchema });

export const adminOfferStateInputSchema = z.object({
  active: z.boolean().optional(),
  defaultForVariant: z.boolean().optional(),
  sortOrder: z.number().int().min(0).max(10_000).optional(),
});

export const brandingSyncResultSchema = z.object({
  branding: brandingSchema,
  appliedFields: z.array(z.string()),
  skippedFields: z.array(z.string()),
  sourceUrl: z.string().url(),
});

export type Address = z.infer<typeof addressSchema>;
export type Plan = z.infer<typeof planSchema>;
export type AdminPlan = z.infer<typeof adminPlanSchema>;
export type SubscriptionStatus = z.infer<typeof subscriptionStatusSchema>;
export type SubscriptionSummary = z.infer<typeof subscriptionSummarySchema>;
export type SubscriptionDetail = z.infer<typeof subscriptionDetailSchema>;
export type NewSubscriptionInput = z.infer<typeof newSubscriptionInputSchema>;
export type AddressUpdateInput = z.infer<typeof addressUpdateInputSchema>;
export type CardUpdateInput = z.infer<typeof cardUpdateInputSchema>;
export type CancelInput = z.infer<typeof cancelInputSchema>;
export type LegalDocument = z.infer<typeof legalDocumentSchema>;
export type PaymentResult = z.infer<typeof paymentResultSchema>;
export type AdminDashboard = z.infer<typeof adminDashboardSchema>;
export type AdminSubscription = z.infer<typeof adminSubscriptionSchema>;
export type AdminSubscriptionSummary = z.infer<typeof adminSubscriptionSummarySchema>;
export type PaymentIssue = z.infer<typeof paymentIssueSchema>;
export type OrderSyncIssue = z.infer<typeof orderSyncIssueSchema>;
export type CustomerSummary = z.infer<typeof customerSummarySchema>;
export type AuditEntry = z.infer<typeof auditEntrySchema>;
export type AdminSettings = z.infer<typeof adminSettingsSchema>;
export type AdminSettingsInput = z.infer<typeof adminSettingsInputSchema>;
export type PlanCatalog = z.infer<typeof planCatalogSchema>;
export type AdminPlanInput = z.infer<typeof adminPlanInputSchema>;
export type IyzicoCatalogProduct = z.infer<typeof iyzicoCatalogProductSchema>;
export type IyzicoCatalogPricingPlan = z.infer<typeof iyzicoCatalogPricingPlanSchema>;
export type CreateIyzicoProductInput = z.infer<typeof createIyzicoProductInputSchema>;
export type CreateIyzicoPricingPlanInput = z.infer<typeof createIyzicoPricingPlanInputSchema>;
export type Branding = z.infer<typeof brandingSchema>;
export type CheckoutOffer = z.infer<typeof checkoutOfferSchema>;
export type CheckoutView = z.infer<typeof checkoutViewSchema>;
export type AddressChoice = z.infer<typeof addressChoiceSchema>;
export type AdminOffer = z.infer<typeof adminOfferSchema>;
export type AdminOfferInput = z.infer<typeof adminOfferInputSchema>;
export type AdminOfferStateInput = z.infer<typeof adminOfferStateInputSchema>;
export type AdminCatalogProduct = z.infer<typeof adminCatalogProductSchema>;
export type AdminCatalogVariant = z.infer<typeof adminCatalogVariantSchema>;
export type AdminProductCatalog = z.infer<typeof adminProductCatalogSchema>;
export type BrandingSyncResult = z.infer<typeof brandingSyncResultSchema>;
